// PidMethodModal.jsx — Add/Edit surface for PID Methods on the shared PidModal scaffold.
// One body, two modes (Create/Edit identical by construction — plan decision 2):
//   create — method picker (methods without a PID entry), staged images, commodity cards
//   edit   — method locked, read-only method details (owned by DataTables → Methods),
//            existing-image delete + staged uploads, commodity cards hydrated from the group
// Save produces the same rows array both old panels produced:
//   [{ MethodUniqueID, CommodityUniqueID, StyleUniqueID, SizeOptionUniqueID }]
// The page keeps its create (POST per row) and edit (delete-reinsert) handlers unchanged.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactSelect from 'react-select';
import { Trash2, X } from 'lucide-react';
import styled from 'styled-components';
import authAxios from '../utils/authAxios';
import PidModal, {
  PrimaryButton, QuietButton,
  MSection, MSectionTitle, MSectionNote,
  DetailGrid, DetailItem,
  ImgGrid, ImgWrap, ImgActionBtn,
} from './PidModal';
import CommodityCardsEditor from './CommodityCardsEditor';
import { ConfirmDelete } from '../styles/TableStyles';
import { customSelectStyles } from '../styles/PackageIndexStyles';

const GridImg = styled.img`
  width: 100%;
  height: 110px;
  object-fit: cover;
  display: block;
`;

const FileInput = styled.input`
  font: inherit;
`;

const CDN_PREFIX = 'https://thevaultmedia.azureedge.net/vault-images';
const FOLDER = 'PIDMethods';

const DETAIL_FIELDS = [
  ['Item Number', 'ItemNumber'],
  ['Type', 'Type'],
  ['Retailer', 'Retailer'],
  ['Description', 'Description'],
  ['Cases/Bin Per Load', 'CasesBinPerLoad'],
  ['Cases/Bin Per Pallet', 'CasesBinPerPallet'],
  ['Ti', 'Ti'],
  ['Hi', 'Hi'],
  ['Length', 'Length'],
  ['Width', 'Width'],
  ['Height', 'Height'],
  ['Net Weight', 'CaseNetWeight'],
  ['UPC', 'UPC'],
];

const sanitizeName = (name) => String(name || '').trim().replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-_]/g, '');

const PidMethodModal = ({ mode, groupData, dropdowns, onClose, onCreate, onSaveEdit, onDeleteGroup }) => {
  const isEdit = mode === 'edit';

  const [selectedMethod, setSelectedMethod] = useState(
    isEdit ? { label: groupData.method, value: groupData.baseInfo.MethodUniqueID } : null
  );
  const [commodityGroups, setCommodityGroups] = useState([]);
  const [existingUrls, setExistingUrls] = useState([]); // full CDN URLs (edit mode)
  const [newFiles, setNewFiles] = useState([]);         // File[] staged until Save
  const [saving, setSaving] = useState(false);

  /* ---------- hydrate the cards from the table group (edit mode) ---------- */
  useEffect(() => {
    if (!isEdit || !groupData) return;
    const formatted = [];
    Object.entries(groupData.commodities || {}).forEach(([commodityLabel, styleMap]) => {
      const matchedCommodity = dropdowns.commodities.find(c => c.label === commodityLabel);
      const styleGroups = Object.entries(styleMap).map(([styleLabel, sizeLabels]) => {
        const matchedStyle = dropdowns.styles.find(s => s.label === styleLabel);
        const sizes = [...new Set(sizeLabels)]
          .map(label => dropdowns.sizeOptions.find(opt => opt.label === label || String(opt.value) === String(label)))
          .filter(Boolean)
          .filter((v, i, a) => a.findIndex(t => t.value === v.value) === i);
        return { style: { label: styleLabel, value: matchedStyle?.value ?? null }, sizes };
      });
      formatted.push({
        commodity: { label: commodityLabel, value: matchedCommodity?.value ?? null },
        styleGroups,
      });
    });
    setCommodityGroups(formatted);
  }, [isEdit, groupData, dropdowns]);

  /* ---------- images (edit mode: load existing) ---------- */
  useEffect(() => {
    if (!isEdit || !groupData?.baseInfo?.MethodUniqueID) return;
    (async () => {
      let urls = [];
      try {
        const apiRes = await authAxios.get('/api/pidmethods/images', {
          params: { methodId: groupData.baseInfo.MethodUniqueID }
        });
        if (Array.isArray(apiRes.data)) {
          urls = apiRes.data.map((p) => {
            const val = String(p);
            if (val.startsWith('http')) return val;
            return `${CDN_PREFIX}/${FOLDER}/${val.replace(/^\/+/, '')}`;
          });
        }
      } catch {
        try {
          const listRes = await authAxios.get('/uploadImage/PIDMethods');
          if (Array.isArray(listRes.data)) {
            const safe = sanitizeName(groupData.method);
            urls = listRes.data.filter(url => url.includes(`/${FOLDER}/${safe}-`));
          }
        } catch { /* ignore */ }
      }
      setExistingUrls(urls);
    })();
  }, [isEdit, groupData]);

  const previews = useMemo(() => newFiles.map(f => ({ url: URL.createObjectURL(f), name: f.name })), [newFiles]);
  useEffect(() => () => previews.forEach(p => URL.revokeObjectURL(p.url)), [previews]);

  const handleAddFiles = (e) => {
    if (e.target.files?.length) setNewFiles(prev => [...prev, ...Array.from(e.target.files)]);
  };

  const deleteExisting = async (url) => {
    try {
      const raw = url.split('/').pop();
      const plain = decodeURIComponent(raw);
      const normalized = encodeURIComponent(plain);
      await authAxios.delete(`/deleteImage/methods/${normalized}`);
      try { await authAxios.delete(`/api/pidmethods/images/${normalized}`); } catch {}
      setExistingUrls(prev => prev.filter(u => u !== url));
    } catch (err) {
      console.error('[Method image delete error]', err);
      alert('Failed to delete image.');
    }
  };

  const uploadStagedImages = useCallback(async () => {
    if (newFiles.length === 0 || !selectedMethod) return;
    const uploaded = [];
    for (const file of newFiles) {
      const fd = new FormData();
      fd.append('image', file);
      fd.append('name', sanitizeName(selectedMethod.label));
      try {
        const up = await authAxios.post(`/uploadImage/${FOLDER}`, fd);
        const blobName = up?.data?.blobName || '';
        const filenameOnly = blobName.split('/').pop();
        if (filenameOnly) uploaded.push(filenameOnly);
      } catch (err) {
        console.error('[Method upload error]', err);
      }
    }
    if (uploaded.length > 0) {
      await authAxios.post('/api/pidmethods/images', {
        MethodUniqueID: selectedMethod.value,
        ImagePaths: uploaded,
      });
    }
    setNewFiles([]);
  }, [newFiles, selectedMethod]);

  /* ---------- save ---------- */
  const buildRows = () => {
    const rows = [];
    for (const group of commodityGroups) {
      const commodityID = group.commodity?.value;
      for (const styleGroup of group.styleGroups || []) {
        const styleID = styleGroup.style?.value;
        for (const size of [...(styleGroup.sizes || [])].sort((a, b) => String(a.label).localeCompare(String(b.label)))) {
          rows.push({
            MethodUniqueID: Number(selectedMethod.value),
            CommodityUniqueID: Number(commodityID),
            StyleUniqueID: Number(styleID),
            SizeOptionUniqueID: Number(size.value),
          });
        }
      }
    }
    return rows;
  };

  const handleSave = async () => {
    if (!selectedMethod) { alert('Select a method first.'); return; }
    const rows = buildRows();
    if (rows.length === 0) {
      alert('Add at least one commodity with a style and size option.');
      return;
    }
    setSaving(true);
    try {
      await uploadStagedImages();
      if (isEdit) await onSaveEdit(rows);
      else await onCreate(rows);
    } catch (err) {
      console.error('[PidMethodModal] save failed', err?.response?.data || err?.message, err);
      alert(err?.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PidModal
      title={isEdit ? `Edit PID Method — ${groupData.method}` : 'New PID Method'}
      status={isEdit ? groupData.baseInfo?.Status : 'Pending'}
      onClose={onClose}
      footerLeft={isEdit ? (
        <ConfirmDelete onConfirm={onDeleteGroup} label="Delete Group" disabled={saving} />
      ) : null}
      footerRight={(
        <>
          <QuietButton type="button" onClick={onClose} disabled={saving}>Cancel</QuietButton>
          <PrimaryButton type="button" onClick={handleSave} disabled={saving}>Save</PrimaryButton>
        </>
      )}
    >
      {isEdit ? (
        <MSection>
          <MSectionTitle>Method Details</MSectionTitle>
          <MSectionNote>These values are maintained in DataTables → Methods and are read-only here.</MSectionNote>
          <DetailGrid>
            {DETAIL_FIELDS.map(([label, key]) => (
              <DetailItem key={key} label={label} value={groupData.baseInfo?.[key]} />
            ))}
          </DetailGrid>
        </MSection>
      ) : (
        <MSection>
          <MSectionTitle>Method</MSectionTitle>
          <MSectionNote>Only methods without an existing PID entry are offered.</MSectionNote>
          <ReactSelect
            options={dropdowns.methods}
            onChange={setSelectedMethod}
            value={selectedMethod}
            placeholder="Select a Method"
            styles={customSelectStyles}
          />
        </MSection>
      )}

      <MSection>
        <MSectionTitle>Images</MSectionTitle>
        {isEdit && (
          existingUrls.length === 0 ? (
            <MSectionNote>No images uploaded yet.</MSectionNote>
          ) : (
            <ImgGrid style={{ marginBottom: 8 }}>
              {existingUrls.map((url) => (
                <ImgWrap key={url}>
                  <GridImg
                    src={url}
                    alt={url.split('/').pop()}
                    onError={(e) => { e.currentTarget.style.opacity = 0.3; }}
                  />
                  <ImgActionBtn $danger title="Delete image" onClick={() => deleteExisting(url)}>
                    <Trash2 size={16} />
                  </ImgActionBtn>
                </ImgWrap>
              ))}
            </ImgGrid>
          )
        )}
        <FileInput type="file" multiple accept=".jpg,.jpeg,.png,.webp" onChange={handleAddFiles} />
        {previews.length > 0 && (
          <>
            <MSectionNote style={{ marginTop: 8 }}>These upload when you click Save.</MSectionNote>
            <ImgGrid style={{ marginTop: 8 }}>
              {previews.map((p, i) => (
                <ImgWrap key={p.url}>
                  <GridImg src={p.url} alt={p.name} />
                  <ImgActionBtn
                    title="Remove from upload"
                    onClick={() => setNewFiles(prev => prev.filter((_, idx) => idx !== i))}
                  >
                    <X size={16} />
                  </ImgActionBtn>
                </ImgWrap>
              ))}
            </ImgGrid>
          </>
        )}
      </MSection>

      {(isEdit || selectedMethod) ? (
        <MSection>
          <MSectionTitle>Commodities, Styles &amp; Sizes</MSectionTitle>
          <CommodityCardsEditor
            groups={commodityGroups}
            onChange={setCommodityGroups}
            commodityOptions={dropdowns.commodities}
            styleOptions={dropdowns.styles}
            sizeOptionsFor={(commodityValue) =>
              dropdowns.sizeOptions.filter(opt => Number(opt.CommodityUniqueID) === Number(commodityValue))}
            hasSizes
          />
        </MSection>
      ) : (
        <MSectionNote>Select a method to configure its commodities, styles, and sizes.</MSectionNote>
      )}
    </PidModal>
  );
};

export default PidMethodModal;
